// A2F-764: Section 1 body — doc type picker.
//
// 4-card grid (one per v1 DocType). Selecting a card swaps the right-
// pane sample. The DocTypePicker pill shape from v1 isn't reused —
// the v2 layout is wider so we render full info cards instead.

import { Check } from 'lucide-react'
import type { DocType } from '../../lib/telemetry-types'

interface DocTypeOption {
  value: DocType
  label: string
  desc: string
}

const OPTIONS: DocTypeOption[] = [
  {
    value: 'fda',
    label: 'FDA submissions',
    desc: 'IND, NDA, BLA — eCTD-ready formatting and CMC table conventions.',
  },
  {
    value: 'protocols',
    label: 'Clinical protocols',
    desc: 'ICH E6 protocol structure, schedule of activities, eligibility lists.',
  },
  {
    value: 'cmc',
    label: 'CMC / lab reports',
    desc: 'Drug substance and drug product characterization, stability, batch.',
  },
  {
    value: 'general',
    label: 'General scientific',
    desc: 'SOPs, technical reports, whitepapers — lighter formatting, full custom rules.',
  },
]

interface SectionDocTypeProps {
  value: DocType
  onChange: (next: DocType) => void
}

export function SectionDocType({ value, onChange }: SectionDocTypeProps) {
  return (
    <div role="radiogroup" aria-label="Document type" className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      {OPTIONS.map((opt) => {
        const selected = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={`relative text-left rounded-lg px-4 py-3 leading-tight transition-colors ${
              selected
                ? 'bg-brand-orange/5 border-2 border-brand-orange text-brand-charcoal'
                : 'bg-white border border-slate-200 text-brand-charcoal hover:border-slate-300'
            }`}
          >
            <div
              className={`text-sm ${selected ? 'font-semibold text-brand-orange' : 'font-medium'}`}
            >
              {opt.label}
            </div>
            <div className="text-[11px] mt-1 text-slate-500 leading-snug">{opt.desc}</div>
            {selected ? (
              <span
                aria-hidden
                className="absolute top-2 right-2 grid h-5 w-5 place-items-center rounded-full bg-brand-orange text-white"
              >
                <Check size={12} strokeWidth={3} />
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  fda: 'FDA submissions',
  protocols: 'Clinical protocols',
  cmc: 'CMC / lab reports',
  general: 'General scientific',
}
